import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarClock, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { ServiceFaqSection } from "@/components/ServiceFaqSection";

const SERVICES = [
  { id: "recording", label: "Recording Session", note: "$50 / hour · vocals & tracking", bookable: true },
  { id: "mixing", label: "Mixing & Mastering", note: "$40/track · $105/3 · $175/5", bookable: false },
  { id: "video", label: "Video / Photography", note: "From $250 · booked personally", bookable: false },
  { id: "custom", label: "Custom / Exclusive Beat", note: "$150–$250 · full rights", bookable: false },
];

const BOOKING_EMAIL = "prod.rushibusiness@gmail.com";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0B0B0E] px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-emerald-300/60";

export default function Booking() {
  const [params] = useSearchParams();
  const [serviceType, setServiceType] = useState(SERVICES[0]);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const s = params.get("service");
    if (!s) return;
    if (/video|photo|vlog/i.test(s)) setServiceType(SERVICES[2]);
    else if (/mix|master|engineering|remaster/i.test(s)) setServiceType(SERVICES[1]);
    else if (/custom|exclusive/i.test(s)) setServiceType(SERVICES[3]);
    else setServiceType(SERVICES[0]);
  }, [params]);

  useEffect(() => {
    setSlot("");
    if (!date) return setSlots([]);
    api.get(`/bookings/availability?date=${date}`).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [date]);

  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) return toast.error("Pick a date and time slot first.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_type: serviceType.label,
        date,
        time_slot: slot,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="booking-page">
      <div className="relative mb-12 overflow-hidden rounded-3xl border border-white/10" data-testid="booking-video-hero">
        <video
          autoPlay muted loop playsInline
          src={fileUrl("/api/files/beatsbyatl/videos/04d7cf88e08e.mp4")}
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="booking-video-loop"
        />
        <div className="absolute inset-0 bg-[#0B0B0E]/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E] via-transparent to-transparent" />
        <Reveal className="relative z-10 px-6 py-16 sm:px-10 sm:py-20">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-300">Blackroom Studios · Atlanta, GA</p>
          <h1 className="font-heading mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Book a Session</h1>
          <p className="mt-4 max-w-xl text-sm text-zinc-300 sm:text-base">
            Recording is <span className="text-emerald-200">$50/hour</span> — your $50 deposit covers the first hour.
            Open Mon–Fri 3PM–12AM, Sat–Sun 4PM–12AM.
          </p>
        </Reveal>
      </div>

      <Reveal>
        <p className="mb-3 font-mono text-xs uppercase tracking-widest text-zinc-500">1 · What are we working on?</p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {SERVICES.map((s) => (
            <button
              type="button" key={s.id}
              data-testid={`booking-service-${s.id}`}
              onClick={() => setServiceType(s)}
              className={`rounded-xl border px-4 py-4 text-left transition-all ${
                serviceType.id === s.id
                  ? "border-emerald-300 bg-emerald-300/10"
                  : "border-white/10 hover:border-white/30"
              }`}
            >
              <span className={`block text-sm font-semibold ${serviceType.id === s.id ? "text-emerald-200" : "text-zinc-300"}`}>{s.label}</span>
              <span className="mt-1 block text-[11px] text-zinc-500">{s.note}</span>
            </button>
          ))}
        </div>
      </Reveal>

      {!serviceType.bookable ? (
        <Reveal delay={0.1}>
          <div className="mt-10 max-w-2xl rounded-3xl border border-emerald-300/30 bg-emerald-300/5 p-8 text-center sm:p-12" data-testid="booking-email-card">
            <Mail size={28} className="mx-auto text-emerald-300" />
            <h2 className="font-heading mt-4 text-2xl font-bold text-white">{serviceType.label} is booked personally</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-zinc-400">
              Rates are flexible and every project is different — email me the details and we'll lock it in together.
              I reply within 24 hours.
            </p>
            <a
              href={`mailto:${BOOKING_EMAIL}?subject=${encodeURIComponent(`Booking inquiry: ${serviceType.label}`)}`}
              data-testid="booking-email-cta"
              className="mt-6 inline-flex items-center gap-3 rounded-full bg-emerald-300 px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 hover:shadow-[0_0_40px_rgba(110,231,183,0.35)]"
            >
              <Mail size={16} /> {BOOKING_EMAIL}
            </a>
          </div>
        </Reveal>
      ) : (
        <form onSubmit={submit} className="mt-10 grid gap-10 lg:grid-cols-5" data-testid="booking-form">
          <div className="space-y-8 lg:col-span-3">
            <Reveal delay={0.05}>
              <div>
                <p className="mb-3 font-mono text-xs uppercase tracking-widest text-zinc-500">2 · Date</p>
                <input
                  type="date" min={today} value={date} required
                  onChange={(e) => setDate(e.target.value)}
                  className={`${inputCls} [color-scheme:dark]`}
                  data-testid="booking-date-picker"
                />
                <p className="mt-2 text-[11px] text-zinc-600">Mon–Fri from 3PM · Sat–Sun from 4PM · last session starts 11PM</p>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div>
                <p className="mb-3 font-mono text-xs uppercase tracking-widest text-zinc-500">3 · Start time (1-hour sessions)</p>
                {date ? (
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="booking-slots-grid">
                    {slots.map((s) => (
                      <button
                        type="button" key={s.time}
                        data-testid={`booking-slot-${s.time.replace(/[^0-9a-z]/gi, "-").toLowerCase()}`}
                        disabled={!s.available}
                        onClick={() => setSlot(s.time)}
                        className={`rounded-xl border px-3 py-3 text-sm font-semibold transition-all ${
                          !s.available
                            ? "cursor-not-allowed border-white/5 text-zinc-700 line-through"
                            : slot === s.time
                              ? "border-emerald-300 bg-emerald-300/10 text-emerald-200"
                              : "border-white/10 text-zinc-300 hover:border-white/30"
                        }`}
                      >
                        {s.time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-white/10 p-6 text-center text-sm text-zinc-600">
                    Select a date to see open hours
                  </p>
                )}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="rounded-3xl border border-white/10 bg-[#141419] p-6 sm:p-8">
              <p className="mb-5 font-mono text-xs uppercase tracking-widest text-zinc-500">4 · Your details</p>
              <div className="space-y-4">
                <input required placeholder="Artist / full name" value={form.name} data-testid="booking-name-input"
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                <input required type="email" placeholder="Email" value={form.email} data-testid="booking-email-input"
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                <input placeholder="Phone" value={form.phone} data-testid="booking-phone-input"
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                <textarea placeholder="Project notes (songs, references, goals)…" rows={4} value={form.notes} data-testid="booking-notes-input"
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-300/20 bg-emerald-300/5 p-4 text-sm" data-testid="booking-summary">
                <div className="flex justify-between text-zinc-400"><span>{serviceType.label} — Blackroom Studios</span></div>
                <div className="mt-1 flex justify-between text-zinc-400">
                  <span>{date || "—"} · starts {slot || "—"}</span>
                </div>
                <div className="mt-3 flex justify-between border-t border-white/10 pt-3 font-bold text-white">
                  <span>Deposit (first hour)</span><span className="text-emerald-200">$50.00</span>
                </div>
                <p className="mt-1 text-right text-[11px] text-zinc-500">then $50/hr at the session</p>
              </div>

              <button
                type="submit" disabled={submitting}
                data-testid="booking-submit-button"
                className="mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-emerald-300 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:bg-emerald-200 disabled:opacity-50"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalendarClock size={16} />}
                {submitting ? "Redirecting to checkout…" : "Pay $50 Deposit & Book"}
              </button>
              <p className="mt-3 text-center text-[11px] text-zinc-600">Secure checkout via Stripe</p>
            </div>
          </Reveal>
        </form>
      )}

      <ServiceFaqSection
        matchTitles={["Recording Session", "Mixing"]}
        heading="Studio FAQ"
        eyebrow="Before you book"
        blurb="Questions artists ask most often about sessions, mixing and turnaround."
        testid="booking-faq"
      />
    </div>
  );
}
